import { URL_PARAMETERS } from "../../../common/utils/const.util";
import { usesFullSessionPricing } from "../../../common/utils/schedule.util";
import { getUrlParameterValue } from "../../../common/utils/url.util";
import { SessionConfig } from "../../../common/utils/session.util";

interface SessionData {
  id: string;
  target_name: string;
  session_name: string;
  session_start: string;
  session_end: string;
  session_number_of_weeks: string;
  session_number_of_classes: string;
  display_on_schedule: string;
  make_default_schedule: string;
  session_notes: string;
  session_holiday_dates: string;
}

let activeSessionData: SessionData | null = null;
let visibleSessions: SessionData[] = [];
let toolbarInitialized = false;
let onSessionChangeCallback: (() => void) | null = null;

/**
 * Initialize the session toolbar. Only runs once — subsequent calls are no-ops.
 * Returns true if the toolbar is visible (has active sessions).
 */
export function initSessionToolbar(onSessionChange: () => void): boolean {
  if (!usesFullSessionPricing((ryb_connect_classes_data as any)?.schedule)) {
    const $toolbar = $("#session-toolbar");
    $toolbar.hide();
    activeSessionData = null;
    visibleSessions = [];
    toolbarInitialized = false;
    return false;
  }

  if (toolbarInitialized) return visibleSessions.length > 0;
  onSessionChangeCallback = onSessionChange;

  const sessions: SessionData[] =
    (ryb_connect_classes_data as any)?.schedule?.session || [];
  const now = new Date();
  const todayStr = formatDateStr(now);

  // Filter: display_on_schedule enabled AND session not yet ended
  visibleSessions = sessions.filter(
    (s) => s.display_on_schedule === "1" && s.session_end >= todayStr
  );

  const $toolbar = $("#session-toolbar");
  if (visibleSessions.length === 0) {
    $toolbar.hide();
    activeSessionData = null;
    return false;
  }

  // Determine active session: URL param > make_default_schedule > first
  const sessionParam = getUrlParameterValue(URL_PARAMETERS.SESSION);
  let active = visibleSessions.find((s) => s.target_name === sessionParam);
  if (!active) {
    active = visibleSessions.find((s) => s.make_default_schedule === "1");
  }
  if (!active) {
    active = visibleSessions[0];
  }
  activeSessionData = active;

  // Set URL param so the filter picks it up
  if (!sessionParam || sessionParam !== active.target_name) {
    const url = new URL(window.location.href);
    url.searchParams.set(URL_PARAMETERS.SESSION, active.target_name);
    history.replaceState({}, "", url);
  }

  renderToolbar($toolbar);
  $toolbar.show();
  toolbarInitialized = true;
  return true;
}

function formatDateStr(d: Date): string {
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

function renderToolbar($toolbar: JQuery): void {
  const $tabs = $toolbar.find(".session-tabs");
  const $infoBar = $toolbar.find(".session-info-bar");
  $tabs.empty();
  $infoBar.empty();

  const now = new Date();
  const todayStr = formatDateStr(now);

  visibleSessions.forEach((session) => {
    const isActive = session.target_name === activeSessionData?.target_name;
    const displayName = session.session_name || session.target_name;
    const isCurrentlyGoing =
      session.session_start <= todayStr && session.session_end >= todayStr;

    const $tab = $(
      `<button class="session-tab${isActive ? " active" : ""}" data-session="${escapeAttr(session.target_name)}">
        <span class="session-tab-label">${escapeHtml(displayName)}</span>
        ${isCurrentlyGoing ? '<span class="session-badge">Ongoing</span>' : ""}
      </button>`
    );

    $tab.on("click", function () {
      if (session.target_name === activeSessionData?.target_name) return;
      activeSessionData = session;

      const url = new URL(window.location.href);
      url.searchParams.set(URL_PARAMETERS.SESSION, session.target_name);
      history.replaceState({}, "", url);

      renderToolbar($toolbar);
      if (onSessionChangeCallback) onSessionChangeCallback();
    });

    $tabs.append($tab);
  });

  if (activeSessionData) {
    renderInfoBar($infoBar, activeSessionData);
  }
}

function renderInfoBar($infoBar: JQuery, session: SessionData): void {
  const items: string[] = [];

  // Date range
  if (session.session_start && session.session_end) {
    const start = formatDisplayDate(session.session_start);
    const end = formatDisplayDate(session.session_end);
    items.push(
      `<div class="session-info-item">
        <div class="session-info-icon-circle">
          <svg class="session-info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>
        </div>
        <div class="session-info-text">
          <span class="session-info-label">Dates</span>
          <span class="session-info-value">${start} – ${end}</span>
        </div>
      </div>`
    );
  }

  // Duration
  if (session.session_number_of_weeks) {
    items.push(
      `<div class="session-info-item">
        <div class="session-info-icon-circle">
          <svg class="session-info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>
        </div>
        <div class="session-info-text">
          <span class="session-info-label">Duration</span>
          <span class="session-info-value">${session.session_number_of_weeks} weeks</span>
        </div>
      </div>`
    );
  }

  // Closed dates
  if (session.session_holiday_dates) {
    const holidayLabel = formatHolidayLabel(session.session_holiday_dates);
    items.push(
      `<div class="session-info-item session-info-holidays">
        <div class="session-info-icon-circle session-info-icon-circle--red">
          <svg class="session-info-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
        </div>
        <div class="session-info-text">
          <span class="session-info-label">Closed Dates</span>
          <span class="session-info-value">${escapeHtml(holidayLabel)}</span>
        </div>
      </div>`
    );
  }

  $infoBar.html(items.join('<div class="session-info-divider"></div>'));
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDisplayDate(dateStr: string): string {
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  const monthIndex = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  return `${MONTH_NAMES[monthIndex]} ${day}`;
}

function formatHolidayLabel(raw: string): string {
  return raw
    .split(",")
    .map((part) => {
      const trimmed = part.trim();
      if (trimmed.includes(":")) {
        const [start, end] = trimmed.split(":");
        return `${formatDisplayDate(start)} – ${formatDisplayDate(end)}`;
      }
      return formatDisplayDate(trimmed);
    })
    .join(", ");
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeAttr(str: string): string {
  return escapeHtml(str);
}

export function getActiveSessionData(): SessionData | null {
  return activeSessionData;
}

export function isSessionToolbarActive(): boolean {
  return toolbarInitialized && visibleSessions.length > 0;
}

export function getActiveSessionConfig(): SessionConfig | null {
  if (!activeSessionData) return null;
  return {
    enabled: true,
    startDate: activeSessionData.session_start,
    endDate: activeSessionData.session_end,
    totalClasses:
      parseInt(activeSessionData.session_number_of_classes, 10) || 12,
    holidayDates: activeSessionData.session_holiday_dates || "",
  };
}
